/**
 * ViewerSyncOverlay
 *
 * - Wendet im Follow-Mode die Kamera des verfolgten Users alle paar
 *   Frames auf window.viewer.scene.view an.
 * - Rendert für alle Remote-User farbige Cursor-Sphären in der Szene.
 * - Zeigt unten links einen Hinweis "Verfolge <name> ×".
 */

import { useEffect, useRef } from 'react'
import { useViewerSyncStore } from '../../store/viewerSync'
import { useAuthStore } from '../../store/auth'

// Lokale Hash-Funktion identisch zu UserAvatar — gleiche Farbe pro User.
const PALETTE = [
  0x0ea5e9, 0x22c55e, 0xf59e0b, 0xef4444, 0x8b5cf6,
  0xec4899, 0x14b8a6, 0xf97316, 0x6366f1, 0x84cc16,
]
function colorFor(id: string): number {
  let h = 0
  for (let i = 0; i < id.length; i++) h = ((h << 5) - h + id.charCodeAt(i)) | 0
  return PALETTE[Math.abs(h) % PALETTE.length]
}

export function ViewerSyncOverlay() {
  const states = useViewerSyncStore((s) => s.states)
  const followingUserId = useViewerSyncStore((s) => s.followingUserId)
  const setFollowing = useViewerSyncStore((s) => s.setFollowing)
  const myId = useAuthStore((s) => s.user?.id)
  const cursorMeshes = useRef<Map<string, any>>(new Map())

  // ── Cursor-Sphären in der Three.js-Szene synchronisieren ──────────────
  useEffect(() => {
    const W = window as any
    const viewer = W.viewer
    const THREE = W.Potree?.THREE || W.THREE
    if (!viewer?.scene?.scene || !THREE) return

    // Map: userId → Mesh. Bei jeder States-Änderung Position/Existenz aktualisieren.
    const scene = viewer.scene.scene
    const meshes = cursorMeshes.current

    // Sphären für aktive Remote-User pflegen
    for (const [userId, state] of Object.entries(states)) {
      if (userId === myId) continue   // eigenen Cursor nicht zeigen
      const cursor = state.cursor
      let mesh = meshes.get(userId)
      if (!cursor) {
        if (mesh) {
          scene.remove(mesh); mesh.geometry?.dispose(); mesh.material?.dispose()
          meshes.delete(userId)
        }
        continue
      }
      const radius = Math.max(0.1, (viewer.scene.view.radius || 10) * 0.005)
      if (!mesh) {
        const geo = new THREE.SphereGeometry(radius, 12, 12)
        const mat = new THREE.MeshBasicMaterial({ color: colorFor(userId), depthTest: false, transparent: true, opacity: 0.85 })
        mesh = new THREE.Mesh(geo, mat)
        mesh.renderOrder = 999  // immer über den Pointclouds zeichnen
        scene.add(mesh)
        meshes.set(userId, mesh)
      }
      // Größe an Szene anpassen
      if (mesh.geometry?.parameters?.radius !== radius) {
        mesh.geometry?.dispose()
        mesh.geometry = new THREE.SphereGeometry(radius, 12, 12)
      }
      mesh.position.set(cursor[0], cursor[1], cursor[2])
    }

    // Verschwundene User: Mesh entfernen
    for (const userId of Array.from(meshes.keys())) {
      if (!states[userId]) {
        const m = meshes.get(userId)
        if (m) {
          scene.remove(m); m.geometry?.dispose(); m.material?.dispose()
        }
        meshes.delete(userId)
      }
    }
  }, [states, myId])

  // Beim Unmount alle Sphären entfernen
  useEffect(() => {
    return () => {
      const W = window as any
      const scene = W.viewer?.scene?.scene
      const meshes = cursorMeshes.current
      if (scene) {
        for (const m of meshes.values()) {
          scene.remove(m); m.geometry?.dispose?.(); m.material?.dispose?.()
        }
      }
      meshes.clear()
    }
  }, [])

  // ── Follow-Mode: Kamera des verfolgten Users anwenden ─────────────────
  useEffect(() => {
    if (!followingUserId) return
    let raf = 0
    const tick = () => {
      const state = useViewerSyncStore.getState().states[followingUserId]
      const W = window as any
      const view = W.viewer?.scene?.view
      if (state?.camera && view) {
        const cam = state.camera
        try {
          view.position.set(cam.position[0], cam.position[1], cam.position[2])
          const THREE = W.Potree?.THREE || W.THREE
          if (THREE) {
            const tgt = new THREE.Vector3(cam.target[0], cam.target[1], cam.target[2])
            view.lookAt(tgt)
          } else {
            view.lookAt(cam.target[0], cam.target[1], cam.target[2])
          }
          if (typeof cam.radius === 'number' && typeof view.radius !== 'undefined') {
            view.radius = cam.radius
          }
        } catch { /* ignore */ }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => { if (raf) cancelAnimationFrame(raf) }
  }, [followingUserId])

  // ── UI-Hinweis unten links ─────────────────────────────────────────────
  if (!followingUserId) return null
  const followedUser = states[followingUserId]
  const label = followedUser?.username || 'User'
  return (
    <div
      style={{
        position: 'fixed', bottom: 16, left: 16, zIndex: 11000,
        background: 'rgba(15,23,42,0.92)', color: '#e2e8f0',
        borderRadius: 999, padding: '8px 14px',
        display: 'flex', alignItems: 'center', gap: 10,
        fontFamily: 'system-ui, sans-serif', fontSize: 13,
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)', pointerEvents: 'auto',
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: `#${colorFor(followingUserId).toString(16).padStart(6, '0')}` }} />
      <span>Verfolge <strong>{label}</strong></span>
      <button
        onClick={() => setFollowing(null)}
        style={{ border: 0, background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}
        title="Verfolgung beenden"
      >×</button>
    </div>
  )
}
